/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useState } from 'react';
import { useAutomationRuleConditions } from '../use-conditions';
import { ConditionItemType, FieldMeetCertainCriteriaErrorsType, FieldMeetCertainCriteriaType } from '../types';
import { fieldMeetCertainCriteriaStyles } from './styles';
import WorkItemFieldInput from './work-item-field';
import OperatorInput from './operators';
import IterationAndAreaPathInput from './iteration-and-area-path';
import UserFieldInput from './user-field';
import SelectInput from './select-input';
import TextInput from './text-input';
import { useDatetimeInputWithConditions } from '../../date-time-input/use-with-conditions';
import DateTimeFormInput from '../../date-time-input';
import { useWorkItemFieldHook } from './work-item-field/use-work-item-field';
import { useAzureProvider } from 'src/azure/context';
import Suspense from 'src/components/shared/suspense-component';
import { useFieldMeetCertainCriteria } from './use-field-meet-certain-criteria';

interface Props {
  conditionId: string;
	validations?: FieldMeetCertainCriteriaErrorsType | null;
}

function FieldMeetCertainCriteriaContent(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();

	const {conditions, setConditions} = useAutomationRuleConditions();
	const {applicationContext} = useAzureProvider();
	
	const fieldMeetCertainCriteria = useFieldMeetCertainCriteria();

	const dateTimeInput = useDatetimeInputWithConditions();
	const useWorkItemFieldInput = useWorkItemFieldHook();

	const [inputs, setInputs] = useState<{
		workItemField: {
			initialOptions: Array<{label: string; value: string}>,
			loading: boolean,
		},
		operator: boolean,
		value: boolean,
	}>({
		workItemField: {
			initialOptions: [],
			loading: false,
		},
		operator: false,
		value: false,
	});
	
	const current = useMemo(() => {
		const found = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return found.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);

	const isUserFieldType = useMemo(() => {
		return current.inputValueType === 'user-field';
	}, [current.inputValueType]);

	const isInOrNotInOperator = useMemo(() => {
		return current.operator === 'SupportedOperations.In' || current.operator === 'SupportedOperations.NotIn';
	}, [current.operator]);

	const isSelectFieldType = useMemo(() => {
		return current.inputValueType === 'select';
	}, [current.inputValueType]);

	const isMultiSelect = useMemo(() => {
		return current.inputValueType === 'multi-select';
	}, [current.inputValueType]);

	const isTextOrNumberInput = useMemo(() => {
		return current.inputValueType === 'number' || current.inputValueType === 'text';
	}, [current.inputValueType]);

	const isDateTimeInput = useMemo(() => {
		return current.inputValueType === 'datetime';
	}, [current.inputValueType]);

	const isAreaPathOrIterationPath = useMemo(() => {
		return current.inputValueType === 'iteration-or-area-path';
	}, [current.inputValueType]);

	const isEmptyOperatorOrNotEmptyOperator = useMemo(() => {
		return ;
	}, [current.operator]);

	const renderInputValue = useMemo(() => {
		return current.operator !== 'CustomOperator.IsEmpty' && current.operator !== 'CustomOperator.IsNotEmpty';
	}, [current.operator, isEmptyOperatorOrNotEmptyOperator]);

	useEffect(() => {
		const asyncHandler = async () => {
			const setWorkItemStateValue = (values: Partial<typeof inputs['workItemField']>) => {
				setInputs((oldValues) => ({
					...oldValues,
					workItemField: {
						...oldValues.workItemField,
						...values
					}
				}));
			};

			setWorkItemStateValue({ loading: true });
			const results = await useWorkItemFieldInput.getWorkItemFieldOptions({
				projectId: applicationContext.projectId,
			});

			setWorkItemStateValue({ loading: false, initialOptions: results });
		};

		asyncHandler();
	}, []);

	return (
		<div className={classes.root}>
			<div className={classes.columnFlex}>
				<div className={classes.columnContent}>
					<Suspense
						isLoading={inputs.workItemField.loading}
					>
						<WorkItemFieldInput
							conditionId={props.conditionId}
							validationError={props.validations?.workItemField || null}
							initialOptions={inputs.workItemField.initialOptions}
							onChangeWorkItemField={(fieldName) => {
								setInputs(oldValue => ({ ...oldValue, operator: true }));
								fieldMeetCertainCriteria.getFieldValueType(fieldName)
									.then((result) => {
										setConditions((oldConditions) => {
											const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
											const others = oldConditions.filter(item => item.id !== props.conditionId);
							
											return [...others, {
												...current, fieldMeetCertainCriteria: {
													...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
													inputValueType: result
												}
											}];
										});
									})
									.finally(() => {
										setInputs((oldValues) => ({ ...oldValues, operator: false }));
									});
							}}
						/>
					</Suspense>
				</div>
			</div>
			<div className={classes.columnFlex}>
				<div className={classes.columnContent}>
					{current.workItemField && (
						<Suspense
							isLoading={inputs.operator}
						>
							<OperatorInput
								conditionId={props.conditionId}
								validationError={props.validations?.operator || null}
							/>
						</Suspense>
					)}
				</div>
			</div>
			<div className={classes.columnFlex}>
				<div className={classes.columnContent}>
					{(current.operator && isAreaPathOrIterationPath && renderInputValue) && (
						<IterationAndAreaPathInput
							conditionId={props.conditionId}
							validationError={props.validations?.iterationOrAreaPathValue || null}
							isMultiSelect={isInOrNotInOperator}
						/>
					)}
					{(current.operator && isUserFieldType && renderInputValue) && (
						<UserFieldInput
							conditionId={props.conditionId}
							validationError={props.validations?.userFieldValue || null}
							isMultiSelect={isInOrNotInOperator}
						/>
					)}
					{(current.operator && isSelectFieldType && renderInputValue) && (
						<SelectInput
							conditionId={props.conditionId}
							validationError={props.validations?.selectValue || null}
							isMultiSelect={isInOrNotInOperator}
						/>
					)}
					{(current.operator && isMultiSelect && renderInputValue) && (
						<SelectInput
							conditionId={props.conditionId}
							validationError={props.validations?.selectValue || null}
							isMultiSelect={true}
						/>
					)}
					{(current.operator && isTextOrNumberInput && renderInputValue) && (
						<TextInput
							type={current.inputValueType as 'text' | 'number'}
							conditionId={props.conditionId}
							validationError={props.validations?.textValue || null}
						/>
					)}
				</div>
				{(current.operator && isDateTimeInput && renderInputValue) && (
					<DateTimeFormInput
						type={current.dateTimeValue.type}
						dateTime={{
							onChange: dateTimeInput.onChangeDateTime(props.conditionId),
							value: current.dateTimeValue.datetime.value,
						}}
						timeframe={{
							onChange: dateTimeInput.onChangeTimeframe(props.conditionId),
							value: {
								timeNumber: current.dateTimeValue.timeframe.timeNumber,
								timeUnit: current.dateTimeValue.timeframe.timeUnit,
								timeOption: current.dateTimeValue.timeframe.timeOption,
							},
						}}
					/>
				)}
			</div>
		</div>
	);
}

const FieldMeetCertainCriteria = memo(FieldMeetCertainCriteriaContent);
export default FieldMeetCertainCriteria;

